import api from './api';

export interface JobStatus {
  name: string;
  isRunning: boolean;
  lastRun?: string;
  nextRun?: string;
  lastResult?: 'success' | 'error' | 'pending';
  message?: string;
}

export interface AllJobsStatus {
  discountCache: JobStatus;
  productListCache: JobStatus;
  [key: string]: JobStatus;
}

export interface ProductListCacheStatus {
  isEnabled: boolean;
  cacheSize: number;
  lastUpdate?: string;
  hitRate?: number;
}

class BackgroundJobsService {
  private baseURL = '/admin/background-jobs';

  /**
   * Get status of all background jobs
   */
  async getStatus(): Promise<AllJobsStatus> {
    const response = await api.get(`${this.baseURL}/status`);
    return (response as any)?.data || response;
  }

  /**
   * Trigger discount cache update
   */
  async triggerDiscountCacheUpdate(): Promise<{ message: string; affected?: number }> {
    const response = await api.post(`${this.baseURL}/discount-cache/trigger`);
    return (response as any)?.data || response;
  }

  /**
   * Update discount for specific product
   */
  async updateProductDiscount(productId: string): Promise<{ message: string }> {
    const response = await api.post(`${this.baseURL}/discount-cache/product/${productId}`);
    return (response as any)?.data || response;
  }

  /**
   * Update discounts for all products of a store
   */
  async updateStoreDiscounts(storeId: string): Promise<{ message: string; affected?: number }> {
    const response = await api.post(`${this.baseURL}/discount-cache/store/${storeId}`);
    return (response as any)?.data || response;
  }

  /**
   * Clean expired discounts
   */
  async cleanExpiredDiscounts(): Promise<{ message: string; cleaned?: number }> {
    const response = await api.post(`${this.baseURL}/discount-cache/cleanup`);
    return (response as any)?.data || response;
  }

  /**
   * Get product list cache status
   */
  async getProductListCacheStatus(): Promise<ProductListCacheStatus> {
    const response = await api.get(`${this.baseURL}/product-list-cache/status`);
    return (response as any)?.data || response;
  }

  /**
   * Trigger product list cache update
   */
  async triggerProductListCacheUpdate(): Promise<{ message: string }> {
    const response = await api.post(`${this.baseURL}/product-list-cache/trigger`);
    return (response as any)?.data || response;
  }

  /**
   * Invalidate product list cache
   */
  async invalidateProductListCache(): Promise<{ message: string }> {
    const response = await api.post(`${this.baseURL}/product-list-cache/invalidate`);
    return (response as any)?.data || response;
  }
}

export const backgroundJobsService = new BackgroundJobsService();
export default backgroundJobsService;

